import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";

/**
 * Artisans organised by the 4 universes.
 * Each artisan uses one of the Maison Marigold watercolor brand assets.
 */

const ARTISANS = [
  // ============ SPICES ============
  {
    id: "a1",
    category: "spices",
    name: "Coopérative des Khasi Hills",
    region_fr: "Jaintia Hills, Meghalaya",
    region_en: "Jaintia Hills, Meghalaya",
    craft_fr: "Curcuma Lakadong · matrilinéaire",
    craft_en: "Lakadong turmeric · matrilineal",
    quote_fr: "« Notre terre garde la mémoire de neuf cents générations. »",
    quote_en: "\"Our earth holds the memory of nine hundred generations.\"",
    image: "/brand/spice-turmeric.jpg",
  },
  {
    id: "a2",
    category: "spices",
    name: "Coorg Highlands Roastery",
    region_fr: "Coorg, Karnataka",
    region_en: "Coorg, Karnataka",
    craft_fr: "Café Monsoon Malabar · torréfaction artisanale",
    craft_en: "Monsoon Malabar coffee · artisanal roasting",
    quote_fr: "« La mousson termine ce que le caféier a commencé. »",
    quote_en: "\"The monsoon finishes what the coffee tree began.\"",
    image: "/brand/spice-coffee.jpg",
  },
  // ============ SAFFRON ============
  {
    id: "a3",
    category: "saffron",
    name: "Coopérative de Lethpora",
    region_fr: "Pampore, Cachemire",
    region_en: "Pampore, Kashmir",
    craft_fr: "Safran Mongra · récolté à l'aube",
    craft_en: "Mongra saffron · harvested at dawn",
    quote_fr: "« Dix-sept jours par an, le crocus nous appelle avant le soleil. »",
    quote_en: "\"Seventeen days a year, the crocus calls us before sunrise.\"",
    image: "/brand/saffron-hero.jpg",
  },
  // ============ TEXTILES ============
  {
    id: "a4",
    category: "textiles",
    name: "Atelier Sharma",
    region_fr: "Bagru, Rajasthan",
    region_en: "Bagru, Rajasthan",
    craft_fr: "Impression au bloc · 5 générations",
    craft_en: "Block printing · 5 generations",
    quote_fr: "« Chaque bloc apprend la main qui le tient. »",
    quote_en: "\"Every block learns the hand that holds it.\"",
    image: "/brand/textile-block-print.jpg",
  },
  {
    id: "a5",
    category: "textiles",
    name: "Bashir Ahmad",
    region_fr: "Srinagar, Cachemire",
    region_en: "Srinagar, Kashmir",
    craft_fr: "Tissage Pashmina · 30 ans",
    craft_en: "Pashmina weaving · 30 years",
    quote_fr: "« Le froid de la montagne est dans chaque fil. »",
    quote_en: "\"The mountain's cold lives in every thread.\"",
    image: "/brand/textile-pashmina.jpg",
  },
  // ============ DECO ART ============
  {
    id: "a6",
    category: "deco",
    name: "Sita Devi",
    region_fr: "Madhubani, Bihar",
    region_en: "Madhubani, Bihar",
    craft_fr: "Peinture Mithila · matrilinéaire",
    craft_en: "Mithila painting · matrilineal",
    quote_fr: "« Ma mère m'a appris. J'apprends à ma fille. »",
    quote_en: "\"My mother taught me. I teach my daughter.\"",
    image: "/brand/deco-madhubani.jpg",
  },
  {
    id: "a7",
    category: "deco",
    name: "Jivya Soma Mashe Atelier",
    region_fr: "Dahanu, Maharashtra",
    region_en: "Dahanu, Maharashtra",
    craft_fr: "Peinture Warli · trois mille ans de mémoire",
    craft_en: "Warli painting · three thousand years of memory",
    quote_fr: "« Trois formes seulement — et le village tout entier. »",
    quote_en: "\"Only three shapes — and an entire village.\"",
    image: "/brand/deco-warli.jpg",
  },
  {
    id: "a8",
    category: "deco",
    name: "Vishal Soni",
    region_fr: "Jaipur, Rajasthan",
    region_en: "Jaipur, Rajasthan",
    craft_fr: "Miniature Mughal · pinceau à un poil",
    craft_en: "Mughal miniature · single-hair brush",
    quote_fr: "« Quatre mois pour un éléphant. Une vie pour un trait. »",
    quote_en: "\"Four months for an elephant. A lifetime for a line.\"",
    image: "/brand/deco-miniature.jpg",
  },
];

const SECTIONS = [
  { key: "spices", labelFr: "Épicerie", labelEn: "Pantry" },
  { key: "saffron", labelFr: "Safran", labelEn: "Saffron" },
  { key: "textiles", labelFr: "Textiles", labelEn: "Textiles" },
  { key: "deco", labelFr: "Art décoratif", labelEn: "Decorative Art" },
];

const ArtisanCard = ({ a, lang, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.7, delay: (index % 2) * 0.08 }}
    className={`group ${index % 2 === 1 ? "md:translate-y-16" : ""}`}
    data-testid={`artisan-${a.id}`}
  >
    <div className="relative aspect-[4/5] overflow-hidden grain bg-[#F2EFE9]">
      <img
        src={a.image}
        alt={a.name}
        className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
      />
    </div>
    <div className="pt-6">
      <p className="overline text-[10px]">{lang === "fr" ? a.region_fr : a.region_en}</p>
      <h3 className="font-serif-display text-3xl md:text-4xl font-light mt-2 text-[#1E2B3C]">
        {a.name}
      </h3>
      <p className="mt-2 text-sm text-[#5C5C5C]">{lang === "fr" ? a.craft_fr : a.craft_en}</p>
      <p className="font-serif-display italic text-xl md:text-2xl mt-5 text-[#B34A36] max-w-md">
        {lang === "fr" ? a.quote_fr : a.quote_en}
      </p>
    </div>
  </motion.article>
);

const Artisans = () => {
  const { t, lang } = useLang();

  return (
    <div className="pt-28 md:pt-36 pb-24" data-testid="page-artisans">
      <section className="px-6 md:px-12 lg:px-24 mb-20 md:mb-28 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <p className="overline mb-5">{t.nav.artisans}</p>
          <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl font-light text-[#1E2B3C] leading-[1.02]">
            {t.artisans.title}
          </h1>
        </div>
        <p className="lg:col-span-5 text-[#5C5C5C] leading-relaxed">{t.artisans.sub}</p>
      </section>

      {SECTIONS.map((section, si) => {
        const list = ARTISANS.filter((a) => a.category === section.key);
        if (list.length === 0) return null;
        return (
          <section
            key={section.key}
            className="px-6 md:px-12 lg:px-24 mb-24 md:mb-32"
            data-testid={`artisans-section-${section.key}`}
          >
            <div className="flex items-end justify-between mb-12 md:mb-16 flex-wrap gap-4 border-t border-[#E5E1D8] pt-10">
              <div>
                <p className="overline mb-3">
                  0{si + 1} · {lang === "fr" ? "Univers" : "Universe"}
                </p>
                <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1E2B3C]">
                  {lang === "fr" ? section.labelFr : section.labelEn}
                </h2>
              </div>
              <Link
                to={section.key === "saffron" ? "/saffron" : `/shop?cat=${section.key}`}
                className="link-underline text-sm tracking-[0.18em] uppercase"
              >
                {lang === "fr" ? "Découvrir" : "Discover"} →
              </Link>
            </div>

            <div className="grid md:grid-cols-2 gap-x-8 gap-y-16 md:gap-y-24">
              {list.map((a, i) => (
                <ArtisanCard key={a.id} a={a} lang={lang} index={i} />
              ))}
            </div>
          </section>
        );
      })}

      <section className="mt-12 text-center px-6">
        <Link to="/shop?cat=spices" className="btn-secondary inline-flex" data-testid="artisans-shop-cta">
          {t.hero.cta}
        </Link>
      </section>
    </div>
  );
};

export default Artisans;
